// ArChat Auto-Normalized File
// Created: 12-03-2026 22:38:36
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * Autor: SIVOL® - Aldo Frias - Argentina
 * Fecha: 11/03/2026 22:50 (Argentina - UTC-3)
 * PlanStatusContext - Fuente única de plan/límites/uso (backend-driven).
 * GET /api/plan/summary.php → plan, limits, usage, percentages.
 * Reemplaza PlanContext: expone plan, isFree, isPro, isLoadingPlan, upgradeToPro.
 * @file frontend/src/context/PlanStatusContext.jsx
 *
 * 🔒 REGLA CRÍTICA PLAN ENGINE
 * Prohibido usar claves legacy:
 * max_*, *_today, storage_mb
 * Solo usar planStatus.limits.* y planStatus.usage.*
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { apiService } from '../services/api';
import { canExportFromPlan } from '../services/conversationExportGate';
import { API_ENDPOINTS } from '../utils/constants';
import { canOperateBilling } from '../utils/paymentsGuard';

const PlanStatusContext = createContext(null);

/** Porcentaje seguro: limit > 0 ? Math.min(100, (usage / limit) * 100) : 0 */
function safePercentage(usage, limit) {
  const u = Number(usage) || 0;
  const l = Number(limit) || 0;
  if (l <= 0) return 0;
  return Math.min(100, (u / l) * 100);
}

export const usePlanStatus = () => {
  const ctx = useContext(PlanStatusContext);
  if (!ctx) {
    return {
      planStatus: null,
      loading: true,
      error: null,
      refetch: () => {},
      plan: 'free',
      isFree: true,
      isPro: false,
      isLoadingPlan: true,
      canExportConversation: false,
      upgradeToPro: async () => ({ success: false, error: 'Servicio no disponible' }),
    };
  }
  return ctx;
};

/** Normaliza respuesta de GET /api/plan/summary.php. Estructura única: limits.{ contacts, chars_month, storage_mb, max_devices }, usage.{ contacts_used, chars_used_month, storage_used_mb, active_devices }, percentages.{ contacts, chars_month, storage, devices }. */
function normalizeSummaryResponse(response) {
  const plan = response?.plan;
  const planCodeStr = plan && (typeof plan.code === 'string' ? plan.code : String(plan.code ?? ''));
  if (!response?.success || !plan || !planCodeStr || !response?.limits || !response?.usage) {
    return null;
  }
  const raw = { limits: response.limits ?? {}, usage: response.usage ?? {}, percentages: response.percentages ?? {} };

  // Preservar max_devices como number; 0 y -1 son válidos (-1 = ilimitado solo si negocio lo define).
  const rawMaxDevices = raw.limits.max_devices;
  const maxDevices = (rawMaxDevices !== null && rawMaxDevices !== undefined && rawMaxDevices !== '')
    ? Number(rawMaxDevices)
    : 0;
  const limits = {
    contacts: Number(raw.limits.contacts) || 0,
    chars_month: Number(raw.limits.chars_month) || 0,
    storage_mb: Number(raw.limits.storage_mb) || 0,
    max_devices: Number.isFinite(maxDevices) ? maxDevices : 0,
  };

  const usage = {
    contacts_used: Number(raw.usage.contacts_used) || 0,
    chars_used_month: Number(raw.usage.chars_used_month) || 0,
    storage_used_mb: Number(raw.usage.storage_used_mb) || 0,
    active_devices: Number(raw.usage.active_devices) || 0,
  };

  const percentages = {
    contacts: Number(raw.percentages.contacts) || 0,
    chars_month: Number(raw.percentages.chars_month) || 0,
    storage: Number(raw.percentages.storage) || 0,
    devices: Number(raw.percentages.devices) || 0,
  };

  const normalized = {
    plan_name: (plan.display_name ?? plan.code ?? 'FREE').toString(),
    plan_code: planCodeStr.toUpperCase(),
    is_founder: !!plan.is_founder,
    limits,
    usage,
    percentages,
    plan: {
      code: planCodeStr,
      slug: plan.slug ?? (planCodeStr ? String(planCodeStr).toLowerCase() : 'free'),
      display_name: plan.display_name ?? planCodeStr,
      effective_code: plan.effective_code,
      is_founder: !!plan.is_founder,
      is_unlimited_user: plan.is_unlimited_user === true,
      founder_position: plan.founder_position ?? null,
      founder_limit: plan.founder_limit ?? 150,
      founder_total: plan.founder_total ?? 150,
      founder_remaining: plan.founder_remaining ?? response.founder_remaining_slots ?? 0,
      is_pro_effective: !!plan.is_pro_effective,
      /** Booleano real desde plan/summary (allow_export); no inferir por código de plan. */
      export_enabled: Boolean(
        plan.export_enabled === true
        || plan.export_enabled === 1
        || plan.export_enabled === '1'
      ),
      tier_rank: typeof plan.tier_rank === 'number' ? plan.tier_rank : 0,
      nextUpgrade: response.next_upgrade ?? null,
      remainingFounderSlots: response.founder_remaining_slots ?? plan.founder_remaining ?? null,
      paddle_activated_at: plan.paddle_activated_at ?? null,
    },
    founder: {
      remaining: response.founder?.remaining ?? plan.founder_remaining ?? response.founder_remaining_slots ?? 0,
      enabled: response.founder?.enabled ?? (Number(plan.founder_remaining ?? response.founder_remaining_slots ?? 0) > 0),
    },
    limits_version: typeof response.limits_version === 'number' ? response.limits_version : undefined,
    /** Solo true si el backend lo envía explícito (allowlist Aldo / sin restricción). */
    billing_operations_allowed: response?.billing_operations_allowed === true,
  };

  // 🔒 DEV SIMULATION – Founder seats
  if (import.meta.env?.DEV) {
    const simulated = import.meta.env?.VITE_DEV_FOUNDER_REMAINING;
    if (typeof simulated !== 'undefined' && simulated !== '') {
      normalized.plan.founder_remaining = Number(simulated);
    }
  }

  if (import.meta.env?.DEV) {
    const legacyKeys = ['messages', 'invitations', 'devices_active', 'max_messages_day', 'max_invitations_per_day'];
    const snapshot = JSON.stringify(normalized);
    if (legacyKeys.some((k) => snapshot.includes(k))) {
      console.error('[PLAN_ENGINE_VIOLATION] Legacy keys detected.');
    }
  }

  return normalized;
}

export function PlanStatusProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const userId = user?.id ?? null;

  const [planStatus, setPlanStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (import.meta.env?.DEV && planStatus) {
      const legacyKeys = ['users', 'messages', 'invitations', 'devices_active'];
      const snapshot = JSON.stringify(planStatus?.limits ?? {}) + JSON.stringify(planStatus?.usage ?? {});
      if (legacyKeys.some((k) => snapshot.includes(k))) {
        console.error('[PLAN_ENGINE_VIOLATION] Legacy keys in limits/usage.');
      }
    }
  }, [planStatus]);

  useEffect(() => {
    if (!planStatus) return;
    const raw = planStatus.billing_operations_allowed;
    const allowed = raw === true;
    try {
      // eslint-disable-next-line no-console
      console.info('[BILLING_UI_STATUS]', { billing_operations_allowed: raw });
      if (allowed) {
        // eslint-disable-next-line no-console
        console.info('[BILLING_UI_SHOW_ACTIONS]', { billing_operations_allowed: true });
      } else {
        // eslint-disable-next-line no-console
        console.info('[BILLING_UI_HIDE_ACTIONS]', { billing_operations_allowed: raw });
      }
    } catch (_) {
      /* noop */
    }
  }, [planStatus]);

  useEffect(() => {
    if (!isAuthenticated || !userId) {
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await apiService.request('/plan/summary.php', {
          _skipDedupe: true,
          _skipGlobalErrorModal: true,
        });
        if (import.meta.env?.DEV) {
          const required = ['contacts', 'chars_month', 'storage_mb', 'max_devices', 'contacts_used', 'chars_used_month', 'storage_used_mb', 'active_devices'];
          const rawStr = JSON.stringify({ limits: data?.limits ?? {}, usage: data?.usage ?? {} });
          const missing = required.filter((k) => !rawStr.includes(k));
          if (missing.length > 0) {
            console.error('[PLAN_ENGINE_ERROR] Missing keys in plan/summary:', missing);
          }
        }
        const normalized = normalizeSummaryResponse(data);

        if (!cancelled) {
          setPlanStatus(normalized);
          if (import.meta.env?.DEV && typeof console?.debug === 'function') {
            console.debug('[PLAN_SYNC] summary=', normalized?.plan_name, normalized?.plan);
          }
        }
      } catch (e) {
        console.error('[PLAN_STATUS] error', e);
        if (!cancelled) {
          setError(e?.message ?? 'Error al cargar plan');
          setPlanStatus(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, userId]);

  const refetch = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.request('/plan/summary.php', {
        _skipDedupe: true,
        _skipGlobalErrorModal: true,
      });
      if (import.meta.env?.DEV) {
        const required = ['contacts', 'chars_month', 'storage_mb', 'max_devices', 'contacts_used', 'chars_used_month', 'storage_used_mb', 'active_devices'];
        const rawStr = JSON.stringify({ limits: data?.limits ?? {}, usage: data?.usage ?? {} });
        const missing = required.filter((k) => !rawStr.includes(k));
        if (missing.length > 0) {
          console.error('[PLAN_ENGINE_ERROR] Missing keys in plan/summary:', missing);
        }
      }
      const normalized = normalizeSummaryResponse(data);
      setPlanStatus(normalized);
      return normalized;
    } catch (e) {
      console.error('[PLAN_STATUS] error', e);
      setError(e?.message ?? 'Error al cargar plan');
      setPlanStatus(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    const onStorageUsageChanged = () => {
      if (!userId) return;
      void refetch();
    };
    window.addEventListener('archat:storage_usage_changed', onStorageUsageChanged);
    return () => window.removeEventListener('archat:storage_usage_changed', onStorageUsageChanged);
  }, [userId, refetch]);

  useEffect(() => {
    if (!userId) return;
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void refetch();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [userId, refetch]);

  const upgradeToPro = useCallback(async (options = {}) => {
    if (!canOperateBilling(planStatus)) {
      return { success: false, code: 'BILLING_TEST_ACCESS_DENIED', message_key: 'plans.payments_disabled.message' };
    }
    const id = user?.id ?? user?.usuario_id;
    if (!id) return { success: false, error: 'Usuario no identificado' };
    const plan = (options?.plan && String(options.plan).toLowerCase() === 'foundation') ? 'foundation' : 'pro';
    try {
      // GET plan/summary persiste csrf_token (api.js) y alinea sesión PHP ↔ X-CSRF-Token antes del POST.
      await refetch();
      const response = await apiService.request(API_ENDPOINTS.UPGRADE_PLAN, {
        method: 'POST',
        body: JSON.stringify({ usuario_id: id, id, plan }),
      });
      if (response && response.success === false) {
        return {
          success: false,
          error: response.message || 'Error al actualizar plan',
          code: response.code,
          message_key: response.message_key,
        };
      }
      await refetch();
      return { success: true };
    } catch (err) {
      console.error('[PLAN_STATUS] upgradeToPro error', err);
      const data = err?.data ?? err?.response;
      const code = data?.code ?? err?.code;
      const message_key = data?.message_key ?? err?.message_key;
      return {
        success: false,
        error: err?.message || 'Error al actualizar plan',
        code: code || undefined,
        message_key: message_key || undefined,
      };
    }
  }, [user?.id, user?.usuario_id, refetch, planStatus]);

  const planSlug = (planStatus?.plan?.slug ?? 'free').toString().toLowerCase();
  const isUnlimitedUser = planStatus?.plan?.is_unlimited_user === true;
  const plan =
    isUnlimitedUser
    || planSlug === 'pro'
    || planSlug === 'foundation'
    || planStatus?.plan?.is_pro_effective
      ? 'pro'
      : 'free';
  const isPro = plan === 'pro';
  const isFree = !isUnlimitedUser && plan === 'free';

  const canExportConversation = useMemo(() => {
    if (loading) return false;
    return canExportFromPlan(planStatus?.plan);
  }, [planStatus, loading]);

  const value = useMemo(() => ({
    planStatus,
    loading,
    error,
    refetch,
    plan,
    isFree,
    isPro,
    isLoadingPlan: loading,
    upgradeToPro,
    canExportConversation,
  }), [planStatus, loading, error, refetch, plan, isFree, isPro, upgradeToPro, canExportConversation]);

  return <PlanStatusContext.Provider value={value}>{children}</PlanStatusContext.Provider>;
}
